<script setup lang="ts">
import type { PanelTab } from '../../../lib/brandAppSections'
import PanelGroup from '../../PanelGroup.vue'
import BlockTitleField from './BlockTitleField.vue'
import { isContentVisible, type BrandAppNews } from '../../../lib/brandAppContract'
import type { CardListItem } from './CardList.vue'
import { NEWS_MAX_COUNT, isTabScreen, newsErrors, reorderNews } from '../../../lib/brandAppNews'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import CardList from './CardList.vue'
import NewsEditor from './NewsEditor.vue'
import useContentEditing from '../../../composables/useContentEditing'

defineProps<{ tab?: PanelTab }>()

const { t } = useI18n()
const { home, updateHome, tabs } = useBrandAppConfig()
const { editing, startEditing, stopEditing } = useContentEditing()
const editorOpen = computed(() => editing.value?.kind === 'actu')
const editingNews = ref<BrandAppNews>()
const pendingDelete = ref<BrandAppNews>()
const newsById = computed(() => Object.fromEntries(home.value.actus.map((news) => [news.id, news])))
const atLimit = computed(() => home.value.actus.length >= NEWS_MAX_COUNT)

const edit = (news?: BrandAppNews) => {
  if (!news && atLimit.value) return
  editingNews.value = news
  startEditing('actu', !news)
}

const save = (news: BrandAppNews) => {
  const exists = home.value.actus.some((item) => item.id === news.id)
  if (!exists && atLimit.value) return
  updateHome(
    'actus',
    exists ? home.value.actus.map((item) => (item.id === news.id ? news : item)) : [...home.value.actus, news]
  )
  closeEditor()
}

const closeEditor = () => {
  stopEditing('actu')
  editingNews.value = undefined
}

onBeforeUnmount(() => stopEditing('actu'))

const remove = () => {
  if (!pendingDelete.value) return
  updateHome(
    'actus',
    home.value.actus.filter((news) => news.id !== pendingDelete.value?.id)
  )
  pendingDelete.value = undefined
}

const destinationLabel = (news: BrandAppNews) =>
  news.ctaTarget.type === 'external'
    ? news.ctaTarget.url
    : t(`pages.loyaltySupports.webapp.news.screens.${news.ctaTarget.screen}`)

const invalidDestination = (news: BrandAppNews) =>
  news.ctaTarget.type === 'screen' &&
  (news.ctaTarget.screen === 'survey'
    ? !news.ctaTarget.surveyId
    : isTabScreen(news.ctaTarget.screen) && !tabs.value.includes(news.ctaTarget.screen))

const cardItems = computed<CardListItem[]>(() =>
  home.value.actus.map((news) => ({
    id: news.id,
    title: news.title,
    subtitle: destinationLabel(news),
    image: news.image,
    hidden: !isContentVisible(news),
    warning: invalidDestination(news) ? t('pages.loyaltySupports.webapp.news.disabledScreen') : undefined
  }))
)

const setVisible = (id: string, isVisible: boolean) => {
  const target = newsById.value[id]
  if (!target) return
  if (isVisible && Object.keys(newsErrors(target, tabs.value)).length > 0) {
    edit(target)
    return
  }
  updateHome(
    'actus',
    home.value.actus.map((news) => (news.id === id ? { ...news, isVisible } : news))
  )
}

const reorder = (ids: string[]) => updateHome('actus', reorderNews(home.value.actus, ids))
</script>

<template>
  <NewsEditor
    v-if="editorOpen"
    class="motion-safe:animate-panel-forward"
    :news="editingNews"
    @save="save"
    @close="closeEditor"
  />

  <PanelGroup v-else-if="tab !== 'content'">
    <BlockTitleField block-id="news" />
  </PanelGroup>

  <PanelGroup
    v-if="!editorOpen && tab !== 'text'"
    :title="t('pages.loyaltySupports.webapp.news.list')"
    :count="`${home.actus.length} / ${NEWS_MAX_COUNT}`"
  >
    <div class="flex flex-col gap-4">
      <CardList
        :items="cardItems"
        :add-label="t('pages.loyaltySupports.webapp.news.add')"
        :can-add="!atLimit"
        @update:order="reorder"
        @update:visible="setVisible"
        @edit="edit(newsById[$event])"
        @remove="pendingDelete = newsById[$event]"
        @add="edit()"
      />
      <p v-if="atLimit" class="text-muted-foreground text-xs">
        {{ t('pages.loyaltySupports.webapp.news.limit', { count: NEWS_MAX_COUNT }) }}
      </p>
    </div>
  </PanelGroup>

  <BaseModal
    :is-open="pendingDelete !== undefined"
    size="sm"
    :heading-title="t('pages.loyaltySupports.webapp.news.delete')"
    @close="pendingDelete = undefined"
  >
    <p class="text-sm">
      {{ t('pages.loyaltySupports.webapp.news.deleteConfirm', { title: pendingDelete?.title ?? '' }) }}
    </p>
    <template #linkBtn>
      <Button variant="ghost" @click="pendingDelete = undefined">
        {{ t('pages.loyaltySupports.webapp.news.cancel') }}
      </Button>
      <Button variant="destructive" @click="remove">{{ t('pages.loyaltySupports.webapp.news.delete') }}</Button>
    </template>
  </BaseModal>
</template>
